// Configuración → Procesos: las Etapas del proceso seleccionado, y los dos
// diálogos que las escriben (alta y edición).
//
// Dos reglas viven aquí enteras:
//
//   - la lista teaches its own model. Una etapa dice de QUIÉN es el trabajo
//     (Responsable) y QUÉ PASA en ella (F2-4) en la misma fila, porque una
//     etapa sin lo segundo se lee como un nombre suelto y no como parte de un
//     proceso.
//   - un delete que SERÍA rechazado se ofrece igual, y contesta con la REGLA
//     antes de que la petición salga del navegador: `stageDeleteBlocker` es la
//     misma cuenta con la que el API guarda su 409.
//
// Las MARCAS del proceso (cita agendada) no se editan aquí: son un hecho del
// proceso y viven en su bloque. La fila las muestra, nada más.

import { useState } from 'react'

import { STAGE_BEHAVIOR_CHIP, STAGE_BEHAVIOR_TONE } from './pipelines-copy.js'
import {
  type AuthoredStageBehavior,
  createStage,
  deleteStage,
  type PipelineFieldView,
  type PipelinesSettings,
  type PipelineStageView,
  type StageBehavior,
  type StageEdit,
  type StageKind,
  type StageOwnerType,
  type StageTeamPolicy,
  stageDeleteBlocker,
  stageOwnerLabel,
  updateStage,
} from './pipelines-data.js'
// Cada comportamiento en las palabras de la fila, y su tono. Esta lista llevaba
// su copia privada de las dos tablas y la vista previa del Agente llevaba la
// otra, a cinco centímetros en la misma pantalla (F4-8): dos maneras de nombrar
// un comportamiento son dos comportamientos para quien lee, y la que se editara
// primero dejaría a la otra mintiendo.
import { ReorderControl } from './ReorderControl.js'
import { type AssignableMemberView } from './settings-data.js'
import {
  Button,
  Checkbox,
  Chip,
  FormField,
  Input,
  Modal,
  type RadioOption,
  RadioGroup,
  Select,
  toast,
} from '../../design-system/index.js'

/** What the three kinds MEAN, in the tenant's terms. The kind is the only
 *  thing the system interprets, so it is the one field that must never be
 *  guessed from its label. */
const KIND_OPTIONS: RadioOption<StageKind>[] = [
  {
    value: 'open',
    label: 'Abierta',
    description:
      'El prospecto sigue en juego: cuenta como trabajo activo de su responsable y genera recordatorios.',
  },
  {
    value: 'won',
    label: 'Ganada',
    description:
      'El recorrido terminó en conversión aquí. Suma a la conversión en Reportes y la tarjeta se pinta en verde.',
  },
  {
    value: 'lost',
    label: 'Perdida',
    description:
      'El recorrido terminó sin conversión. Deja de contar como trabajo activo y la tarjeta se pinta en rojo.',
  },
]

const KIND_LABEL: Record<StageKind, string> = {
  open: 'Abierta',
  won: 'Ganada',
  lost: 'Perdida',
}

const KIND_TONE: Record<StageKind, 'info' | 'ok' | 'draft'> = {
  open: 'info',
  won: 'ok',
  lost: 'draft',
}

/** LO QUE PASA EN LA ETAPA (F2-4), en las tres respuestas que el Agente sabe
 *  honrar hoy. Es una pregunta distinta de "Responsable": aquella dice de quién
 *  es el trabajo, esta dice qué ocurre con la conversación. Sin ella una etapa
 *  de traspaso no podía existir y el proceso compilado se despedía justo donde
 *  la clínica pasa la llamada con una persona.
 *
 *  Solo se dibuja en las etapas ABIERTAS: en una ganada o perdida el final lo
 *  marca el tipo de etapa, y el servidor deriva `fin`. */
const BEHAVIOR_OPTIONS: RadioOption<AuthoredStageBehavior>[] = [
  {
    value: 'captura',
    label: 'El Agente pregunta los datos',
    description: 'El Agente hace las preguntas de esta etapa y guarda las respuestas.',
  },
  {
    value: 'llenado_manual',
    label: 'Una persona la llena',
    description: 'El Agente no pregunta nada aquí: los datos los captura tu equipo.',
  },
  {
    value: 'agenda',
    label: 'El Agente agenda la cita',
    description:
      'El Agente consulta los horarios de tus doctores, ofrece los más cercanos y agenda la ' +
      'cita. La tarjeta pasa a esta etapa con la fecha.',
  },
  {
    value: 'handoff',
    label: 'La conversación pasa con una persona',
    description: 'El Agente deja de responder y la conversación queda con tu equipo.',
  },
]

/** Por qué "Una persona la llena" está apagada: la base de datos exige un
 *  responsable humano para esa etapa, así que la opción no se ofrece sin él.
 *  Se dice DONDE está apagada, no al guardar. */
const BEHAVIOR_MANUAL_DISABLED = 'Elige una persona o un equipo como responsable.'
/** Por qué "El Agente agenda la cita" está apagada, y por qué el responsable se
 *  queda en el Agente cuando ya lo está (D22-9). EL ACOPLE VA EN LOS DOS
 *  SENTIDOS, así que es UNA frase y no dos: la misma que el motor contesta
 *  (`assertStageBehavior`), para que la pantalla y el servidor no expliquen la
 *  misma regla con dos juegos de palabras. */
const BEHAVIOR_AGENDA_NEEDS_BOT =
  'Esta etapa agenda la cita, así que la atiende el Agente. Elige al Agente como responsable, o ' +
  'cambia lo que pasa en la etapa.'
/** Por qué en el ALTA no se puede elegir. Una etapa nace sin la marca de cita
 *  agendada, y agendar la exige (`pipeline_stages_behavior_agenda_commitment_
 *  check`): la marca se mueve al editar, así que ahí se elige. */
const BEHAVIOR_AGENDA_ON_CREATE =
  'La etapa que agenda la cita se elige al editarla, cuando ya es parte del proceso.'
/** Lo que guardar "El Agente agenda la cita" hace ADEMÁS: la marca de cita
 *  agendada pasa a esta etapa. No es un efecto secundario que se descubra
 *  después, es la misma decisión: donde se agenda es donde se cumple la promesa,
 *  y la base de datos no deja que sean dos columnas distintas. */
const AGENDA_TAKES_MARKER = 'Al guardar, la marca de cita agendada pasa a esta etapa.'
/** Lo único que pasa en una etapa cerrada, dicho en lugar del selector. */
const CLOSED_STAGE_BEHAVIOR = 'Etapa final: aquí termina el proceso.'
/** La pregunta, con las mismas palabras en los dos diálogos. */
const BEHAVIOR_LABEL_TEXT = '¿Qué pasa en esta etapa?'

/** Lo que el SERVIDOR derivaría para este responsable, salvo un traspaso ya
 *  elegido, que se respeta: la misma regla que `nextStageBehavior` aplica en el
 *  motor cuando el patch no trae behavior. Dicha aquí para que el radio nunca
 *  muestre una respuesta distinta de la que se guardaría. */
function behaviorForOwner(
  ownerType: StageOwnerType,
  current: AuthoredStageBehavior,
): AuthoredStageBehavior {
  // Lo que la derivación NO puede producir sobrevive al cambio de responsable,
  // que es la regla (4) de `nextStageBehavior` en el motor: está en la columna
  // porque alguien lo eligió, y ningún par (tipo, responsable) lo repondría.
  if (current === 'handoff' || current === 'agenda') return current
  return ownerType === 'bot' ? 'captura' : 'llenado_manual'
}

/** ¿Es este comportamiento uno de los que el diálogo sabe DECIR? Los demás
 *  (`inicio`, `multiplexor`, `fin`) se muestran en la fila pero no tienen radio,
 *  así que el diálogo no puede hablar de ellos. */
function isAuthoredBehavior(b: StageBehavior): b is AuthoredStageBehavior {
  return b === 'captura' || b === 'llenado_manual' || b === 'handoff' || b === 'agenda'
}

/** Lo que la etapa hace hoy, si el diálogo sabe decirlo.
 *
 *  SE DEVUELVE TAL CUAL para TODO valor autorable (D22-9), y la caída sólo cubre
 *  los tres que no tienen radio. Antes se nombraban a mano los dos que se
 *  conservaban, así que `agenda` — que sí es del conjunto desde D22-1 — se
 *  colapsaba a lo que el radio supiera mostrar: el diálogo abría diciendo
 *  "El Agente pregunta los datos" sobre una etapa que agenda. La lista a mano es
 *  la regla que pierde el siguiente valor; preguntar por el conjunto no. */
function authoredBehaviorOf(s: PipelineStageView): AuthoredStageBehavior {
  if (isAuthoredBehavior(s.behavior)) return s.behavior
  return behaviorForOwner(s.ownerType, 'captura')
}

/** ONE question: quién se hace responsable de un prospecto en esta etapa. Las
 *  tres respuestas son las tres formas de elegir persona, no tres mecanismos
 *  distintos: "una persona" es una manera más de resolverlo, junto a las
 *  políticas del equipo. */
const OWNER_OPTIONS: RadioOption<StageOwnerType>[] = [
  {
    value: 'bot',
    label: 'El Agente',
    description: 'El Agente atiende esta etapa. Nadie del equipo recibe asignación automática.',
  },
  {
    value: 'member',
    label: 'Una persona',
    description: 'Al entrar un prospecto sin responsable a esta etapa, se asigna a esta persona.',
  },
  {
    value: 'team',
    label: 'El equipo',
    description: 'El equipo atiende esta etapa. Elige cómo se reparte el trabajo.',
  },
]

/** Qué hace cada opción fija, en una línea. Se muestra bajo el Select para que
 *  la consecuencia se lea sin abrir otra pestaña. Una política nombrada tiene
 *  su propia línea (abajo), porque sus reglas viven en Operación. */
const TEAM_POLICY_HELP: Record<string, string> = {
  balanced: 'Se asigna a quien tenga menos trabajo activo.',
  none: 'Los prospectos entran sin responsable; alguien los toma manualmente.',
}
const NAMED_POLICY_HELP = 'Se asigna según las reglas de esta política, definidas en Operación.'
/** Dónde nacen las políticas, dicho cuando todavía no hay ninguna. */
const NO_POLICIES_HINT = 'Crea políticas en Configuración → Operación.'

/** Dónde entran los prospectos nuevos, dicho en la lista de etapas: la primera
 *  etapa abierta es la puerta, no una marca aparte. */
const FIRST_OPEN_HINT =
  'Los prospectos nuevos entran a la primera etapa abierta, salvo que un flujo indique otra etapa.'

/** The stage edit modal's draft. `requiredFields` holds field KEYS — the
 *  picker shows labels from the lead catalog and stores keys (F19). */
interface StageDraft {
  name: string
  kind: StageKind
  ownerType: StageOwnerType
  ownerMemberId: string
  /** La fuente de asignación del equipo en UN valor de Select: 'balanced',
   *  'none', o el id (uuid) de una política nombrada. Un uuid no puede
   *  confundirse con las dos palabras fijas. */
  teamSource: string
  requiredFields: string[]
  /** Lo que la etapa obtiene. Misma forma que requiredFields (claves de la
   *  ficha), significado distinto: cuenta, no exige. */
  gatheredFields: string[]
  /** Qué pasa en la etapa (F2-4). Se guarda siempre en el borrador, y sólo
   *  viaja cuando la etapa es abierta: en una cerrada el final lo marca el
   *  tipo. */
  behavior: AuthoredStageBehavior
}

function draftOf(s: PipelineStageView): StageDraft {
  return {
    name: s.name,
    kind: s.kind,
    ownerType: s.ownerType,
    ownerMemberId: s.ownerMemberId ?? '',
    // null y 'none' significan lo mismo; el Select lo dice con una sola opción.
    teamSource: s.teamPolicyId ?? s.teamPolicy ?? 'none',
    requiredFields: [...s.requiredFields],
    gatheredFields: [...s.gatheredFields],
    behavior: authoredBehaviorOf(s),
  }
}

export interface StageListProps {
  /** El proceso seleccionado, o `''` cuando el espacio todavía no tiene ninguno. */
  pipelineId: string
  stages: PipelineStageView[]
  /** A quién puede nombrar responsable una etapa. */
  members: AssignableMemberView[]
  /** Las políticas nombradas del espacio: una opción por política en el
   *  sub-Select de "El equipo". */
  policies: { id: string; name: string }[]
  /** La FICHA de este proceso: el guardado sólo deja pasar claves que siga
   *  llevando. */
  ficha: PipelineFieldView[]
  /** La misma ficha sin los datos ocultos del catálogo: los selectores del
   *  diálogo sólo pueden ofrecer datos ACTIVOS. */
  fichaActive: PipelineFieldView[]
  /** Clave → etiqueta del catálogo, para leer "Requiere:" y "Obtiene:" en
   *  palabras y no en claves. */
  fieldLabelOf: Map<string, string>
  /** Hay una escritura del proceso en curso. */
  busy: boolean
  /** El guardado compartido de la pantalla: siembra la caché con el tablero que
   *  devolvió y avisa con la frase del API cuando falla. */
  run: (fn: () => Promise<PipelinesSettings>, okMsg: string) => Promise<PipelinesSettings | null>
}

export function StageList({
  pipelineId,
  stages,
  members,
  policies,
  ficha,
  fichaActive,
  fieldLabelOf,
  busy,
  run,
}: StageListProps): JSX.Element {
  const [stageCreateOpen, setStageCreateOpen] = useState(false)
  const [newStageName, setNewStageName] = useState('')
  const [newStageKind, setNewStageKind] = useState<StageKind>('open')
  // Una etapa nace del Agente (el servidor la crea sin responsable), así que
  // aquí sólo caben dos de las tres respuestas; la tercera se explica apagada.
  const [newStageBehavior, setNewStageBehavior] = useState<AuthoredStageBehavior>('captura')
  const [editTarget, setEditTarget] = useState<PipelineStageView | null>(null)
  const [draft, setDraft] = useState<StageDraft | null>(null)
  const [stageDeleteTarget, setStageDeleteTarget] = useState<PipelineStageView | null>(null)

  const stageDeleteReason = stageDeleteTarget ? stageDeleteBlocker(stageDeleteTarget, stages) : null

  async function submitStageCreate(): Promise<void> {
    const name = newStageName.trim()
    if (pipelineId === '' || name === '') {
      toast.error('Escribe un nombre para la etapa.')
      return
    }
    if (
      await run(
        () =>
          createStage(pipelineId, {
            name,
            kind: newStageKind,
            // Sólo las abiertas dicen qué pasa en ellas: en una ganada o
            // perdida el final lo marca el tipo y el servidor lo deriva.
            ...(newStageKind === 'open' ? { behavior: newStageBehavior } : {}),
          }),
        'Etapa agregada al final del proceso.',
      )
    ) {
      setStageCreateOpen(false)
      setNewStageName('')
      setNewStageKind('open')
      setNewStageBehavior('captura')
    }
  }

  async function submitStageEdit(): Promise<void> {
    if (pipelineId === '' || !editTarget || !draft) return
    if (draft.ownerType === 'member' && draft.ownerMemberId === '') {
      toast.error('Elige a la persona responsable de esta etapa.')
      return
    }
    // Las marcas NO viajan en este patch: se eligen en el proceso, con su
    // propio Select. Reenviarlas aquí sería una segunda fuente de verdad para
    // un hecho que solo puede tener una.
    //
    // La fuente del equipo viaja en UNA de sus dos formas (el API las declara
    // excluyentes): las palabras fijas van en teamPolicy y un uuid va en
    // teamPolicyId; la otra viaja en null para limpiarla. En las etapas que no
    // son del equipo se limpian las dos, igual que la persona.
    const isNamedPolicy =
      draft.ownerType === 'team' && draft.teamSource !== 'balanced' && draft.teamSource !== 'none'
    // ¿El diálogo movió el comportamiento? Sólo puede decir que sí sobre una
    // etapa cuyo comportamiento actual es de los tres que el radio expresa: en
    // los demás el borrador arranca en un valor DERIVADO, no en el de la fila,
    // y compararlos diría "cambió" sin que nadie haya tocado nada.
    const behaviorMoved =
      isAuthoredBehavior(editTarget.behavior) && draft.behavior !== editTarget.behavior
    const edit: StageEdit = {
      name: draft.name.trim(),
      kind: draft.kind,
      ownerType: draft.ownerType,
      ownerMemberId: draft.ownerType === 'member' ? draft.ownerMemberId : null,
      teamPolicy:
        draft.ownerType === 'team' && !isNamedPolicy ? (draft.teamSource as StageTeamPolicy) : null,
      teamPolicyId: isNamedPolicy ? draft.teamSource : null,
      // Solo claves que la FICHA DE ESTE PROCESO todavía lleva: el API rechaza
      // las demás, y una regla que ningún dato de este proceso puede
      // satisfacer no merece sobrevivir a la edición.
      requiredFields: draft.requiredFields.filter((k) => ficha.some((f) => f.key === k)),
      gatheredFields: draft.gatheredFields.filter((k) => ficha.some((f) => f.key === k)),
      // Qué pasa en la etapa viaja sólo en las abiertas (F2-4) y sólo cuando el
      // diálogo lo CAMBIÓ (D22-1). Mandarlo en una cerrada sería pedirle a la
      // base algo que sus CHECKs no permiten: ahí el final lo marca el tipo de
      // etapa. Y mandarlo sin haberlo movido convierte cualquier edición — el
      // nombre, la ficha, el responsable — en una escritura del comportamiento:
      // sobre una etapa cuyo comportamiento este diálogo no sabe decir
      // (`inicio` o `multiplexor`), `authoredBehaviorOf` cae a lo que el radio
      // sí sabe mostrar y la guardaría así. El servidor ya conserva lo que no le
      // mandan; callar aquí es lo que hace que esa caída no tenga por dónde
      // llegar. Desde D22-9 `agenda` ya NO es uno de esos casos: el radio la
      // dice, así que elegirla de verdad la MANDA.
      ...(draft.kind === 'open' && behaviorMoved ? { behavior: draft.behavior } : {}),
    }
    if (await run(() => updateStage(pipelineId, editTarget.id, edit), 'Etapa actualizada.')) {
      setEditTarget(null)
      setDraft(null)
    }
  }

  async function submitStageDelete(): Promise<void> {
    if (pipelineId === '' || !stageDeleteTarget) return
    if (await run(() => deleteStage(pipelineId, stageDeleteTarget.id), 'Etapa eliminada.')) {
      setStageDeleteTarget(null)
    }
  }

  /** Move a stage one slot. Sent as "put it at index N" rather than as a swap,
   *  which is what the API normalises positions from. */
  async function moveStage(stage: PipelineStageView, delta: number): Promise<void> {
    if (pipelineId === '') return
    const index = stages.findIndex((s) => s.id === stage.id)
    const next = index + delta
    if (next < 0 || next >= stages.length) return
    await run(
      () => updateStage(pipelineId, stage.id, { position: next }),
      'Cambiamos el orden de las etapas.',
    )
  }

  function ownerLabel(s: PipelineStageView): string {
    return stageOwnerLabel(
      s.ownerType,
      s.teamPolicy,
      members.find((m) => m.id === s.ownerMemberId)?.name ?? null,
      policies.find((p) => p.id === s.teamPolicyId)?.name ?? null,
    )
  }

  /** Everything the row says about a stage, as ONE string. Built here rather
   *  than as sibling nodes so it truncates as a sentence instead of wrapping a
   *  word per line when the column is narrow. */
  function metaLine(s: PipelineStageView): string {
    const parts = [`Responsable: ${ownerLabel(s)}`]
    // Las claves guardadas se muestran con su ETIQUETA del catálogo (F19); una
    // clave sin campo activo se muestra tal cual antes que ocultar la regla.
    if (s.requiredFields.length > 0) {
      parts.push(`Requiere: ${s.requiredFields.map((k) => fieldLabelOf.get(k) ?? k).join(', ')}`)
    }
    // "Obtiene:" va DESPUÉS de "Requiere:" a propósito: primero lo que la etapa
    // exige para dejar entrar, luego lo que se consigue una vez dentro.
    if (s.gatheredFields.length > 0) {
      parts.push(`Obtiene: ${s.gatheredFields.map((k) => fieldLabelOf.get(k) ?? k).join(', ')}`)
    }
    parts.push(
      s.leadCount === 0
        ? 'Sin prospectos'
        : `${s.leadCount} ${s.leadCount === 1 ? 'prospecto' : 'prospectos'}`,
    )
    return parts.join(' · ')
  }

  return (
    <>
      <div className="rounded-xl border border-line bg-surface p-5" data-testid="stage-list">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-[13px] font-semibold text-text">Etapas</h2>
            {/* W4'-6c: el orden se explica junto a las flechas que lo mueven. Es
                el orden REAL del compilador (`byStageOrder` en capture-plan.ts,
                por `position`): el recorrido de la tarjeta y el orden en que el
                agente pregunta, etapa por etapa. */}
            <p className="mt-1 text-[12px] text-text-2">
              En el orden en que aparecen en el tablero de Procesos: es el recorrido de la tarjeta y
              el orden en que el agente pregunta, etapa por etapa. {FIRST_OPEN_HINT}
            </p>
          </div>
          <Button
            variant="secondary"
            disabled={pipelineId === '' || busy}
            onClick={() => setStageCreateOpen(true)}
          >
            Agregar etapa
          </Button>
        </div>

        <ul className="mt-4 flex flex-col gap-2">
          {stages.map((s, index) => {
            const blocker = stageDeleteBlocker(s, stages)
            return (
              <li
                key={s.id}
                data-testid={`stage-row-${s.key}`}
                // Three columns, and the middle one is the only one that gives:
                // minmax(0,1fr) lets it shrink so its lines truncate, instead of
                // the flex row squeezing it into a word-per-line column.
                className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-lg border border-line p-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
              >
                {/* El orden de la etapa, con el mismo control que la ficha
                    (F2-6b A): dos flechas de verdad bajo la palabra que dice
                    qué son. Los glifos de texto no se entendían. */}
                <ReorderControl
                  label={s.name}
                  upDisabled={index === 0 || busy}
                  downDisabled={index === stages.length - 1 || busy}
                  onMoveUp={() => void moveStage(s, -1)}
                  onMoveDown={() => void moveStage(s, 1)}
                />

                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold text-text">
                    <span className="truncate">{s.name}</span>
                    <Chip tone={KIND_TONE[s.kind]}>{KIND_LABEL[s.kind]}</Chip>
                    {/* Qué PASA en la columna (F2-4), en palabras: es lo que
                        hace que la lista se lea como un proceso y no como una
                        lista de nombres. */}
                    <Chip tone={STAGE_BEHAVIOR_TONE[s.behavior]}>
                      {STAGE_BEHAVIOR_CHIP[s.behavior]}
                    </Chip>
                    {/* Report only. The marker is edited on the process block. */}
                    {s.isCommitment && <Chip tone="ok">Cita agendada</Chip>}
                  </p>
                  <p className="mt-0.5 truncate font-mono text-[11px] text-text-3">{s.key}</p>
                  <p
                    className="mt-0.5 truncate text-[11.5px] text-text-2"
                    data-testid={`stage-meta-${s.key}`}
                  >
                    {metaLine(s)}
                  </p>
                </div>

                <div className="col-span-2 flex flex-none items-center justify-end gap-2 sm:col-span-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    aria-label={`Editar ${s.name}`}
                    onClick={() => {
                      setEditTarget(s)
                      setDraft(draftOf(s))
                    }}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    {...(blocker === null ? {} : { title: blocker })}
                    aria-label={`Eliminar ${s.name}`}
                    onClick={() => setStageDeleteTarget(s)}
                  >
                    Eliminar
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      {/* Agregar etapa */}
      <Modal
        open={stageCreateOpen}
        onClose={() => setStageCreateOpen(false)}
        title="Agregar etapa"
        subtitle="Se agrega al final del proceso; puedes moverla con las flechas."
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStageCreateOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitStageCreate()}
            >
              {busy ? 'Agregando…' : 'Agregar etapa'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <FormField label="Nombre" htmlFor="stage-name">
            <Input
              id="stage-name"
              placeholder="Cotización enviada"
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
            />
          </FormField>
          <RadioGroup
            label="Tipo de etapa"
            name="new-stage-kind"
            value={newStageKind}
            options={KIND_OPTIONS}
            onChange={(v) => setNewStageKind(v)}
          />
          {/* Qué pasa en la etapa (F2-4). La etapa nace del Agente, así que
              "Una persona la llena" llega apagada CON su razón: se prende
              eligiendo responsable en Editar etapa. */}
          {newStageKind === 'open' ? (
            <div data-testid="new-stage-behavior">
              <RadioGroup
                label={BEHAVIOR_LABEL_TEXT}
                name="new-stage-behavior"
                value={newStageBehavior}
                options={BEHAVIOR_OPTIONS.map((o) =>
                  o.value === 'llenado_manual' || o.value === 'agenda'
                    ? { ...o, disabled: true }
                    : o,
                )}
                onChange={(v) => setNewStageBehavior(v)}
              />
              <p className="mt-2 text-[11px] text-text-3">{BEHAVIOR_MANUAL_DISABLED}</p>
              {/* D22-9: agendar exige la marca de cita agendada, y una etapa
                  nace sin ella. Se dice DONDE está apagada, no al guardar. */}
              <p className="mt-1 text-[11px] text-text-3">{BEHAVIOR_AGENDA_ON_CREATE}</p>
            </div>
          ) : (
            <p className="text-[11px] text-text-3" data-testid="new-stage-behavior-closed">
              {CLOSED_STAGE_BEHAVIOR}
            </p>
          )}
        </div>
      </Modal>

      {/* Editar etapa */}
      <Modal
        open={editTarget !== null}
        onClose={() => {
          setEditTarget(null)
          setDraft(null)
        }}
        title="Editar etapa"
        subtitle={editTarget ? editTarget.key : ''}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setEditTarget(null)
                setDraft(null)
              }}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitStageEdit()}
            >
              Guardar
            </Button>
          </>
        }
      >
        {draft && editTarget && (
          <div className="flex flex-col gap-4">
            <FormField label="Nombre" htmlFor="edit-stage-name">
              <Input
                id="edit-stage-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </FormField>

            <div>
              <RadioGroup
                label="Tipo de etapa"
                name="edit-stage-kind"
                value={draft.kind}
                options={KIND_OPTIONS}
                onChange={(v) => setDraft({ ...draft, kind: v })}
              />
              {editTarget.isCommitment && draft.kind !== 'open' && (
                <p className="mt-2 text-[11px] text-text-3">
                  Esta etapa tiene una marca que solo aplica a etapas abiertas. Mueve la marca a
                  otra etapa antes de cerrarla.
                </p>
              )}
            </div>

            {/* UNA sola pregunta: quién se hace responsable aquí. El sub-Select
                de "El equipo" es parte de la misma respuesta, no una segunda
                decisión, por eso vive dentro del bloque y no en otra tarjeta. */}
            <div className="flex flex-col gap-3">
              <RadioGroup
                label="Responsable"
                name="edit-stage-owner"
                value={draft.ownerType}
                // EL ACOPLE VA EN LOS DOS SENTIDOS (D22-9). Una etapa que
                // agenda la atiende el Agente (lo exige el CHECK
                // `pipeline_stages_behavior_agenda_owner_check`), así que las
                // otras dos respuestas se apagan CON la misma razón con la que
                // se apaga agendar cuando el responsable es humano. Sin esto la
                // pantalla ofrecía una pareja que el guardado iba a rechazar.
                options={OWNER_OPTIONS.map((o) =>
                  draft.behavior === 'agenda' && o.value !== 'bot' ? { ...o, disabled: true } : o,
                )}
                // Cambiar de responsable re-deriva qué pasa en la etapa, con la
                // MISMA regla del servidor: un traspaso ya elegido se respeta;
                // lo demás sigue al responsable. Si no, el radio se quedaría en
                // una respuesta que el guardado iba a rechazar.
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    ownerType: v,
                    behavior: behaviorForOwner(v, draft.behavior),
                  })
                }
              />
              {draft.behavior === 'agenda' && (
                <p className="text-[11px] text-text-3" data-testid="edit-stage-owner-agenda">
                  {BEHAVIOR_AGENDA_NEEDS_BOT}
                </p>
              )}
              {draft.ownerType === 'member' && (
                <FormField label="Persona" htmlFor="edit-stage-member">
                  {members.length === 0 ? (
                    <p className="text-[12px] text-text-2">
                      Todavía no hay asesores a quienes asignar. Invita a alguien en la pestaña
                      Equipo.
                    </p>
                  ) : (
                    <Select
                      id="edit-stage-member"
                      placeholder="Elige a una persona…"
                      options={members.map((m) => ({ value: m.id, label: m.name }))}
                      value={draft.ownerMemberId}
                      onChange={(e) => setDraft({ ...draft, ownerMemberId: e.target.value })}
                    />
                  )}
                </FormField>
              )}
              {draft.ownerType === 'team' && (
                <FormField label="Cómo se elige a la persona" htmlFor="edit-stage-team-policy">
                  <Select
                    id="edit-stage-team-policy"
                    options={[
                      { value: 'balanced', label: 'Equilibrada' },
                      { value: 'none', label: 'Sin asignación automática' },
                      // Una opción por política nombrada, por su nombre.
                      ...policies.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                    value={draft.teamSource}
                    onChange={(e) => setDraft({ ...draft, teamSource: e.target.value })}
                  />
                  <p className="mt-2 text-[11px] text-text-3">
                    {TEAM_POLICY_HELP[draft.teamSource] ?? NAMED_POLICY_HELP}
                  </p>
                  {policies.length === 0 && (
                    <p className="mt-1 text-[11px] text-text-3">{NO_POLICIES_HINT}</p>
                  )}
                </FormField>
              )}
              {/* La semántica de entrada, dicha sin rodeos. Es la pregunta que
                  el equipo hace siempre: cuándo se dispara, desde cuándo aplica
                  y a quién respeta (F23: el cambio mira hacia adelante). */}
              <p className="text-[11px] leading-relaxed text-text-3">
                La asignación ocurre cuando un prospecto entra a la etapa sin responsable, lo mueva
                el Agente o una persona. Se aplica a los prospectos que entren a la etapa de ahora
                en adelante. Los que ya tienen responsable no cambian.
              </p>
            </div>

            {/* QUÉ PASA EN LA ETAPA (F2-4). La segunda mitad de la etapa: arriba
                se dijo de quién es el trabajo, aquí qué ocurre con la
                conversación. Sólo tiene sentido en una etapa abierta; en una
                cerrada se dice la única respuesta posible en una línea, en vez
                de dibujar un selector con una sola opción. */}
            {draft.kind === 'open' ? (
              <div data-testid="edit-stage-behavior">
                <RadioGroup
                  label={BEHAVIOR_LABEL_TEXT}
                  name="edit-stage-behavior"
                  value={draft.behavior}
                  // "Una persona la llena" exige un responsable humano (lo pide
                  // la base de datos), así que con el Agente al frente se apaga
                  // CON su razón, en lugar de aceptarse y fallar al guardar.
                  // Las dos exclusiones son la MISMA regla dicha desde sus dos
                  // lados: llenar a mano exige un responsable humano, agendar
                  // exige al Agente. Cada una se apaga CON su razón, en lugar de
                  // aceptarse y fallar al guardar.
                  options={BEHAVIOR_OPTIONS.map((o) => {
                    if (o.value === 'llenado_manual' && draft.ownerType === 'bot') {
                      return { ...o, disabled: true }
                    }
                    if (o.value === 'agenda' && draft.ownerType !== 'bot') {
                      return { ...o, disabled: true }
                    }
                    return o
                  })}
                  onChange={(v) => setDraft({ ...draft, behavior: v })}
                />
                {draft.ownerType === 'bot' ? (
                  <p className="mt-2 text-[11px] text-text-3">{BEHAVIOR_MANUAL_DISABLED}</p>
                ) : (
                  <p className="mt-2 text-[11px] text-text-3">{BEHAVIOR_AGENDA_NEEDS_BOT}</p>
                )}
                {/* LA MARCA SIGUE A LA ETAPA QUE AGENDA (D22-9). Agendar la
                    exige, así que guardar la MUEVE aquí en la misma escritura:
                    se dice antes de apretar, no después. */}
                {draft.behavior === 'agenda' && !editTarget.isCommitment && (
                  <p
                    className="mt-1 text-[11px] text-text-3"
                    data-testid="edit-stage-agenda-marker"
                  >
                    {AGENDA_TAKES_MARKER}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-text-3" data-testid="edit-stage-behavior-closed">
                {CLOSED_STAGE_BEHAVIOR}
              </p>
            )}

            {/* Datos requeridos (F19 + CP3 K): un selector sobre la FICHA DE
                ESTE PROCESO, no sobre la biblioteca del espacio — se muestran
                etiquetas, se guardan claves, y el API rechaza las que no son
                parte de la ficha. Una etapa solo puede exigir lo que su propio
                proceso captura. Un prospecto solo entra a la etapa con estos
                datos capturados. */}
            <div data-testid="edit-stage-required">
              <p className="text-[12px] font-medium text-text">Datos requeridos</p>
              {fichaActive.length === 0 ? (
                <p className="mt-1.5 text-[12px] text-text-2">
                  Para requerir datos en esta etapa, primero agrégalos a este proceso en Datos del
                  proceso.
                </p>
              ) : (
                <>
                  <p className="mt-1 text-[11px] text-text-3">
                    Un prospecto solo puede entrar a esta etapa cuando estos datos ya están
                    capturados. Las citas agendadas entran siempre.
                  </p>
                  <div className="mt-2 flex flex-col gap-1.5">
                    {fichaActive.map((f) => (
                      <Checkbox
                        key={f.key}
                        label={f.label}
                        checked={draft.requiredFields.includes(f.key)}
                        onChange={(checked) =>
                          setDraft({
                            ...draft,
                            requiredFields: checked
                              ? [...draft.requiredFields, f.key]
                              : draft.requiredFields.filter((k) => k !== f.key),
                          })
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Se obtiene en esta etapa (CP3 L). El gemelo narrativo del bloque
                de arriba, sobre la MISMA ficha: aquel dice qué hace falta para
                entrar, este dice qué se consigue una vez dentro. No cambia
                ninguna regla de entrada, y el ayudante lo dice para que nadie lo
                use esperando que bloquee algo. */}
            <div data-testid="edit-stage-gathered">
              <p className="text-[12px] font-medium text-text">Se obtiene en esta etapa</p>
              {fichaActive.length === 0 ? (
                <p className="mt-1.5 text-[12px] text-text-2">
                  Para decir qué se obtiene en esta etapa, primero agrega datos a este proceso en
                  Datos del proceso.
                </p>
              ) : (
                <>
                  <p className="mt-1 text-[11px] text-text-3">
                    Qué datos se consiguen aquí, los pida el Agente o los capture tu equipo. Es
                    informativo: ordena la ficha y guía los flujos.
                  </p>
                  <div className="mt-2 flex flex-col gap-1.5">
                    {fichaActive.map((f) => (
                      <Checkbox
                        key={f.key}
                        label={f.label}
                        checked={draft.gatheredFields.includes(f.key)}
                        onChange={(checked) =>
                          setDraft({
                            ...draft,
                            gatheredFields: checked
                              ? [...draft.gatheredFields, f.key]
                              : draft.gatheredFields.filter((k) => k !== f.key),
                          })
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Eliminar etapa — or the rule that refuses it, said here rather than
          under the row, where it would be permanent noise. */}
      <Modal
        open={stageDeleteTarget !== null}
        onClose={() => setStageDeleteTarget(null)}
        role="alertdialog"
        title="Eliminar etapa"
        subtitle={stageDeleteTarget ? stageDeleteTarget.name : ''}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setStageDeleteTarget(null)}
              disabled={busy}
            >
              {stageDeleteReason === null ? 'Cancelar' : 'Entendido'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={busy || stageDeleteReason !== null}
              onClick={() => void submitStageDelete()}
            >
              Eliminar etapa
            </Button>
          </>
        }
      >
        {stageDeleteReason !== null ? (
          <p
            className="text-[12.5px] leading-relaxed text-text-2"
            data-testid={`stage-blocker-${stageDeleteTarget?.key ?? ''}`}
          >
            No puedes eliminar esta etapa: {stageDeleteReason}
          </p>
        ) : (
          <p className="text-[12.5px] leading-relaxed text-text-2">
            {stageDeleteTarget
              ? `Se quita "${stageDeleteTarget.name}" del proceso. Esta acción no se puede deshacer.`
              : ''}
          </p>
        )}
      </Modal>
    </>
  )
}
